import { MetricsSection } from './components/metrics-section';
import { ControlledDatePicker } from '@/components/date-picker';
import { AppActions, AppTitle, AppTitleText } from '@/components/app-header/app-header';
import { RefreshButton } from '@/components/refresh-button';
import { useState } from 'react';
import { DataTable } from './data-table';
import { columns } from './columns';
import { CreateCampaignDialog } from './components/create-campaign-dialog';
import { useCampaigns, useCreateCampaign } from './hooks/use-campaigns';
import { useParams, useNavigate } from 'react-router-dom';
import { transformCampaignData } from './utils/transform-campaign';
import { useToast } from '@/hooks/use-toast';
import { useUser } from '@clerk/clerk-react';
import { Channel } from '@/gql/graphql';
import { Campaign } from './types';
import { ColumnDef } from '@tanstack/react-table';
export default function CampaignsPage() {
  const { slug: merchantId } = useParams();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const { toast } = useToast();
  const { user } = useUser();
  const navigate = useNavigate();

  const {
    campaigns,
    loading: isLoading,
    error: campaignsError,
    refetch
  } = useCampaigns(merchantId ?? '');
  const { create: createCampaign, loading: isCreating } = useCreateCampaign(merchantId ?? '');

  const { campaigns: data, metrics, channelBreakdowns } = transformCampaignData(campaigns);

  const handleCreateCampaign = async ({ name }: { name: string }) => {
    if (!merchantId || !user) return;

    try {
      // Create a default SMS variant
      const defaultVariant = {
        channel: Channel.Sms,
        variantName: 'Variant 1',
        splitWeight: 100, // 100% since it's the only variant
        content: {},
        metadata: {}
      };

      const campaign = await createCampaign({
        merchantId,
        name,
        createdBy: user.id,
        segments: { include: [], exclude: [] },
        variants: [defaultVariant]
      });

      if (!campaign?.campaignId) {
        throw new Error('Failed to create campaign - no ID returned');
      }

      // Navigate to the campaign wizard with both name and ID
      void navigate(
        `/merchants/${merchantId}/campaigns/create/audience?name=${encodeURIComponent(name)}&id=${campaign.campaignId}`
      );

      toast({
        title: 'Success',
        description: `Campaign "${name}" created successfully`
      });

      void refetch();
    } catch (error) {
      console.error('Failed to create campaign:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create campaign';
      toast({
        variant: 'destructive',
        title: 'Error',
        description: errorMessage
      });
    }
  };

  const handleRowClick = (campaign: Campaign) => {
    if (!merchantId) return;

    // For draft campaigns, navigate to the wizard editor
    if (campaign.status === 'draft' || campaign.status === 'scheduled') {
      void navigate(
        `/merchants/${merchantId}/campaigns/create/audience?name=${encodeURIComponent(campaign.name)}&id=${campaign.campaignId}`
      );
      return;
    }

    // For other statuses, navigate to the campaign details view
    void navigate(`/merchants/${merchantId}/campaigns/${campaign.campaignId}`);
  };

  return (
    <div className="space-y-6">
      <AppTitle>
        <AppTitleText>Campaigns</AppTitleText>
      </AppTitle>
      <AppActions>
        <RefreshButton
          onClick={() => {
            void refetch();
          }}
          isRefreshing={isLoading}
        />
      </AppActions>
      <AppActions>
        <ControlledDatePicker />
      </AppActions>
      <MetricsSection
        messagesSent={metrics.messagesSent}
        unsubscribed={0}
        clicks={metrics.clicks}
        ctr={metrics.ctr}
        orders={metrics.orders}
        cvr={metrics.cvr}
        revenue={metrics.revenue}
        epm={metrics.epm}
        channelBreakdowns={channelBreakdowns}
      />
      <DataTable
        columns={columns as ColumnDef<Campaign>[]}
        data={data}
        isLoading={isLoading || isCreating}
        error={
          campaignsError
            ? {
                message:
                  campaignsError instanceof Error
                    ? campaignsError.message
                    : 'An unknown error occurred',
                retry: () => void refetch()
              }
            : undefined
        }
        onCreateClick={() => {
          setShowCreateDialog(true);
        }}
        onRowClick={handleRowClick}
      />
      <CreateCampaignDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSubmit={handleCreateCampaign}
      />
    </div>
  );
}
